import React from "react";
import { Box, Container, Heading, Text, VStack, SimpleGrid, Button, Icon, List, ListItem, ListIcon } from "@chakra-ui/react";
import { FaSolarPanel, FaDollarSign, FaHome, FaArrowRight, FaCheckCircle, FaTint } from "react-icons/fa";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";
import { useNavigate } from "react-router-dom";

const Solutions: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solutions"
        fallbackTitle="The Prosperity Engine™ — Integrated Solutions"
        fallbackDescription="Water, solar, finance, and property — four systems working as one. The Prosperity Engine™ by One Group Australia."
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Text fontSize="sm" color="rgba(255, 255, 255, 0.7)" textTransform="uppercase" letterSpacing="wider">
              The Prosperity Engine™
            </Text>
            <Heading size="2xl" fontWeight="bold">
              Four Systems. One Strategy.
            </Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="2xl">
              Water, solar, finance, and property — each system strengthens the next. This is how long-term prosperity is built.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Four Core Systems */}
      <Container maxW="container.xl" py={20}>
        <Heading size="xl" textAlign="center" mb={12} color={brandConfig.colors.primary}>
          The Prosperity Engine™ — Four Core Systems
        </Heading>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10}>
          {/* Water */}
          <Box
            p={8}
            bg="white"
            borderRadius="xl"
            boxShadow="xl"
            transition="all 0.3s"
            _hover={{ transform: "translateY(-8px)", boxShadow: "2xl" }}
            borderTop="4px solid"
            borderColor="#6FAFD6"
          >
            <Icon as={FaTint} w={12} h={12} color="#6FAFD6" mb={4} />
            <Heading size="md" mb={4} color={brandConfig.colors.primary}>
              Water
            </Heading>
            <Text color="gray.600" mb={6}>
              Understand what's in your water before you filter it. One Water Australia takes a research-first approach — assessing your water source and household health priorities before making any recommendation.
            </Text>
            <List spacing={3} mb={6}>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="#6FAFD6" />
                Research-backed water assessment
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="#6FAFD6" />
                Under-sink and whole-home filtration
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="#6FAFD6" />
                UV purification systems
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="#6FAFD6" />
                No-pressure, education-first approach
              </ListItem>
            </List>
            <Button
              w="full"
              bg={brandConfig.colors.primary}
              color="white"
              _hover={{ bg: brandConfig.colors.primaryHover }}
              onClick={() => navigate("/water")}
            >
              Learn More
            </Button>
          </Box>

          {/* Solar Energy */}
          <Box
            p={8}
            bg="white"
            borderRadius="xl"
            boxShadow="xl"
            transition="all 0.3s"
            _hover={{ transform: "translateY(-8px)", boxShadow: "2xl" }}
            borderTop="4px solid"
            borderColor="#C69214"
          >
            <Icon as={FaSolarPanel} w={12} h={12} color="#C69214" mb={4} />
            <Heading size="md" mb={4} color={brandConfig.colors.primary}>
              Solar
            </Heading>
            <Text color="gray.600" mb={6}>
              Solar reduces your energy costs and builds your energy independence. Premium panel installations with battery options, backed by a 25-year performance warranty.
            </Text>
            <List spacing={3} mb={6}>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="#C69214" />
                Tier 1 solar panels
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="#C69214" />
                Battery storage options
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="#C69214" />
                25-year performance warranties
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="#C69214" />
                CEC-accredited installation
              </ListItem>
            </List>
            <Button
              w="full"
              bg={brandConfig.colors.primary}
              color="white"
              _hover={{ bg: brandConfig.colors.primaryHover }}
              onClick={() => navigate("/solutions/solar-property")}
            >
              Learn More
            </Button>
          </Box>

          {/* Finance Solutions */}
          <Box
            p={8}
            bg="white"
            borderRadius="xl"
            boxShadow="xl"
            transition="all 0.3s"
            _hover={{ transform: "translateY(-8px)", boxShadow: "2xl" }}
            borderTop="4px solid"
            borderColor="#2F3A4A"
          >
            <Icon as={FaDollarSign} w={12} h={12} color="#2F3A4A" mb={4} />
            <Heading size="md" mb={4} color={brandConfig.colors.primary}>
              Finance
            </Heading>
            <Text color="gray.600" mb={6}>
              Structure your borrowing across the full market. Our independent brokers search 40+ lenders to find the right home loan, investment loan, or asset finance for your situation — at no upfront cost.
            </Text>
            <List spacing={3} mb={6}>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="#2F3A4A" />
                Independent, not bank-aligned
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="#2F3A4A" />
                Home and investment loans
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="#2F3A4A" />
                Solar and asset financing
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="#2F3A4A" />
                SMSF loan structuring
              </ListItem>
            </List>
            <Button
              w="full"
              bg={brandConfig.colors.primary}
              color="white"
              _hover={{ bg: brandConfig.colors.primaryHover }}
              onClick={() => navigate("/solutions/finance-solar")}
            >
              Learn More
            </Button>
          </Box>

          {/* Property Investment */}
          <Box
            p={8}
            bg="white"
            borderRadius="xl"
            boxShadow="xl"
            transition="all 0.3s"
            _hover={{ transform: "translateY(-8px)", boxShadow: "2xl" }}
            borderTop="4px solid"
            borderColor="#0D7F6E"
          >
            <Icon as={FaHome} w={12} h={12} color="#0D7F6E" mb={4} />
            <Heading size="md" mb={4} color={brandConfig.colors.primary}>
              Property
            </Heading>
            <Text color="gray.600" mb={6}>
              Build long-term wealth through strategic property investment. From first homes to investment portfolios, our specialists provide structured guidance grounded in data and experience.
            </Text>
            <List spacing={3} mb={6}>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="#0D7F6E" />
                First home buyers
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="#0D7F6E" />
                Investment property strategy
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="#0D7F6E" />
                Portfolio growth planning
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="#0D7F6E" />
                SMSF property acquisition
              </ListItem>
            </List>
            <Button
              w="full"
              bg={brandConfig.colors.primary}
              color="white"
              _hover={{ bg: brandConfig.colors.primaryHover }}
              onClick={() => navigate("/solutions/wealth-building")}
            >
              Learn More
            </Button>
          </Box>
        </SimpleGrid>
      </Container>

      {/* Integrated Solutions Section */}
      <Box bg="gray.50" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={8} mb={12}>
            <Heading size="xl" textAlign="center" color={brandConfig.colors.primary}>
              How the Systems Connect
            </Heading>
            <Text fontSize="lg" textAlign="center" maxW="3xl" color="gray.600">
              Each system strengthens the next. That's what makes The Prosperity Engine™ more than the sum of its parts.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
            <Box p={8} bg="white" borderRadius="lg" boxShadow="md">
              <Heading size="md" mb={4} color={brandConfig.colors.primary}>
                Solar + Property
              </Heading>
              <Text color="gray.600" mb={4}>
                Add solar to your investment property to increase rental appeal, reduce vacancy, and boost assessed value for future financing.
              </Text>
              <Button
                size="sm"
                variant="ghost"
                color={brandConfig.colors.primary}
                _hover={{ bg: "rgba(20, 28, 73, 0.08)" }}
                rightIcon={<Icon as={FaArrowRight} />}
                onClick={() => navigate("/solutions/solar-property")}
              >
                Explore Solar + Property
              </Button>
            </Box>

            <Box p={8} bg="white" borderRadius="lg" boxShadow="md">
              <Heading size="md" mb={4} color={brandConfig.colors.primary}>
                Finance + Solar
              </Heading>
              <Text color="gray.600" mb={4}>
                Access solar financing that works alongside your home loan structure. A coordinated approach means your energy and borrowing strategies reinforce each other.
              </Text>
              <Button
                size="sm"
                variant="ghost"
                color={brandConfig.colors.primary}
                _hover={{ bg: "rgba(20, 28, 73, 0.08)" }}
                rightIcon={<Icon as={FaArrowRight} />}
                onClick={() => navigate("/solutions/finance-solar")}
              >
                Explore Finance + Solar
              </Button>
            </Box>

            <Box p={8} bg="white" borderRadius="lg" boxShadow="md">
              <Heading size="md" mb={4} color={brandConfig.colors.primary}>
                First Home + Solar
              </Heading>
              <Text color="gray.600" mb={4}>
                Enter the property market with a solar-ready home. Combine first home buyer benefits with solar savings for a strong start to home ownership.
              </Text>
              <Button
                size="sm"
                variant="ghost"
                color={brandConfig.colors.primary}
                _hover={{ bg: "rgba(20, 28, 73, 0.08)" }}
                rightIcon={<Icon as={FaArrowRight} />}
                onClick={() => navigate("/solutions/first-home-solar")}
              >
                Explore First Home + Solar
              </Button>
            </Box>

            <Box p={8} bg="white" borderRadius="lg" boxShadow="md">
              <Heading size="md" mb={4} color={brandConfig.colors.primary}>
                Refinance + Solar
              </Heading>
              <Text color="gray.600" mb={4}>
                Refinance to a better rate and upgrade to solar in one coordinated process — reducing both your mortgage cost and your ongoing energy expenses.
              </Text>
              <Button
                size="sm"
                variant="ghost"
                color={brandConfig.colors.primary}
                _hover={{ bg: "rgba(20, 28, 73, 0.08)" }}
                rightIcon={<Icon as={FaArrowRight} />}
                onClick={() => navigate("/solutions/refinance-solar-battery")}
              >
                Explore Refinance + Solar
              </Button>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Heading size="xl">
              Where Would You Like to Start?
            </Heading>
            <Text fontSize="lg" maxW="2xl" color="rgba(255, 255, 255, 0.9)">
              Book a free consultation to explore which system is the right entry point for your situation. Our specialists will map out how the pieces connect.
            </Text>
            <Button
              size="lg"
              bg="white"
              color={brandConfig.colors.primary}
              _hover={{ bg: "gray.100", transform: "translateY(-2px)" }}
              rightIcon={<Icon as={FaArrowRight} />}
              onClick={() => navigate("/contact")}
              transition="all 0.2s"
            >
              Book Free Consultation
            </Button>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default Solutions;
